import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  Button,
  Grid,
  Divider,
  Tabs,
  Tab,
  Paper,
  TextField,
  IconButton,
  Snackbar,
  Alert,
  Rating,
  useMediaQuery,
  Chip,
  useTheme,
  Breadcrumbs,
  Dialog,
  DialogContent,
  Skeleton,
} from '@mui/material';
import LW_Embeddable from '../integrations/LW_Embeddable';
import EmbeddedComponentError from '../error/EmbeddedComponentError';
import { logError } from '@utils/errorLogger';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { search } from '@api/search';
import { formatPrice, generatePlaceholderImageUrl } from '@utils/formatter';
import { loadCart, saveCart } from '@utils/cartUtils';
import { useFaq } from '@context/FaqContext';
import ErrorBoundary from '../error/ErrorBoundary';
import pdfIcon from '@assets/pdf.png';

function ProductDetailSkeleton() {
  return (
    <Container maxWidth='lg' sx={{ py: 3 }}>
      {/* Breadcrumb skeleton */}
      <Box sx={{ mb: 3 }}>
        <Skeleton variant='text' height={24} width='60%' />
      </Box>

      {/* Main product section skeleton */}
      <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden', mb: 4 }}>
        <Grid container sx={{ minHeight: '400px' }}>
          {/* Image section skeleton */}
          <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
            <Box
              sx={{
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                flex: 1,
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  overflow: 'hidden',
                  bgcolor: 'grey.50',
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Skeleton variant='rectangular' width='100%' height={300} />
              </Paper>
            </Box>
          </Grid>

          {/* Product info section skeleton */}
          <Grid item xs={12} md={8} sx={{ display: 'flex' }}>
            <Box
              sx={{
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                flex: 1,
              }}
            >
              <Paper
                variant='outlined'
                sx={{
                  p: 2.5,
                  bgcolor: 'grey.50',
                  borderRadius: 2,
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
              >
                {/* Title skeleton */}
                <Skeleton
                  variant='text'
                  height={40}
                  width='80%'
                  sx={{ mb: 1 }}
                />

                {/* Brand and rating skeleton */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    mb: 1.5,
                  }}
                >
                  <Skeleton variant='text' height={20} width='30%' />
                  <Skeleton variant='rectangular' height={20} width={120} />
                </Box>

                {/* Variant options skeleton */}
                <Box sx={{ mb: 2 }}>
                  <Skeleton
                    variant='text'
                    height={20}
                    width='40%'
                    sx={{ mb: 1 }}
                  />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {[1, 2, 3, 4].map((i) => (
                      <Skeleton
                        key={i}
                        variant='rectangular'
                        width={60}
                        height={70}
                      />
                    ))}
                  </Box>
                </Box>

                {/* Price skeleton */}
                <Skeleton
                  variant='text'
                  height={50}
                  width='40%'
                  sx={{ mb: 2 }}
                />

                <Divider sx={{ my: 1.5 }} />

                {/* Quantity and cart button skeleton */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Skeleton variant='rectangular' width={70} height={40} />
                  <Skeleton variant='rectangular' width='60%' height={40} />
                </Box>
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs skeleton */}
      <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex' }}>
            {[1, 2, 3].map((i) => (
              <Skeleton
                key={i}
                variant='rectangular'
                width='33.33%'
                height={48}
              />
            ))}
          </Box>
        </Box>
        <Box sx={{ p: 2 }}>
          <Skeleton variant='text' height={20} width='100%' sx={{ mb: 1 }} />
          <Skeleton variant='text' height={20} width='80%' sx={{ mb: 1 }} />
          <Skeleton variant='text' height={20} width='60%' />
        </Box>
      </Paper>
    </Container>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [selectedImage, setSelectedImage] = useState(0);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const { isFaqVisible } = useFaq();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const showSnackbar = (message) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  useEffect(() => {
    const loadProductData = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await search({
          id,
          rows: 1,
          queryProfile: 'pdp',
          collapse: true,
          collapseField: 'group',
          expand: true,
          expandField: 'image_url',
        });

        if (result.success && result.documents?.length > 0) {
          const productData = result.documents[0];

          // Merge expanded image variants into the product
          const expanded = result.data?.expanded || {};
          const groupId = productData.group || productData.id;
          const expandedDocs = expanded[groupId]?.docs || [];

          console.log('[ProductDetail] Expanded data for product:', {
            productId: productData.id,
            groupId,
            expandedDocs,
            variantImages: expandedDocs
              .map((doc) => doc.image_url)
              .filter(Boolean),
          });

          // Collect variant data with both image URLs and SKUs from expanded docs
          let variantData = expandedDocs
            .filter((doc) => doc.image_url)
            .map((doc) => ({
              image_url: doc.image_url,
              sku: doc.sku || doc.id,
              title: doc.title,
              id: doc.id,
              product_id: doc.product_id,
            }));

          // Fallback: If no expanded docs and we have a group, search for other products in the same group
          if (variantData.length === 0 && productData.group) {
            try {
              const groupResult = await search({
                query: `group:'${productData.group}'`,
                rows: 20,
                queryProfile: 'pdp',
              });

              if (groupResult.success && groupResult.documents) {
                variantData = groupResult.documents
                  .filter((p) => p.image_url)
                  .map((p) => ({
                    image_url: p.image_url,
                    sku: p.sku || p.id,
                    title: p.title,
                    id: p.id,
                    product_id: p.product_id,
                  }));

                console.log(
                  '[ProductDetail] Fallback group search: Found',
                  variantData.length,
                  'variant items'
                );
              }
            } catch (error) {
              console.warn(
                '[ProductDetail] Fallback group search failed:',
                error
              );
            }
          }

          // Always include the main product as first variant if not already included
          const mainVariant = {
            image_url: productData.image_url,
            sku: productData.sku || productData.id,
            title: productData.title,
            id: productData.id,
            product_id: productData.product_id,
          };

          if (
            productData.image_url &&
            !variantData.some((v) => v.image_url === productData.image_url)
          ) {
            variantData.unshift(mainVariant);
          }

          // Extract just the image URLs for backward compatibility
          const variantImages = variantData.map((v) => v.image_url);

          const productWithVariants = {
            ...productData,
            variant_images:
              variantImages.length > 0
                ? variantImages
                : productData.variant_images || [],
            variant_data: variantData.length > 0 ? variantData : [mainVariant],
          };

          setProduct(productWithVariants);
          setSelectedImage(0);
        } else {
          throw new Error(result.error || 'Product not found');
        }
      } catch (error) {
        setError(error.message || 'Failed to load product');
      } finally {
        setLoading(false);
      }
    };
    loadProductData();
  }, [id]);

  const handleTabChange = (_, newValue) => setActiveTab(newValue);
  const handleQuantityChange = (e) => {
    const v = parseInt(e.target.value);
    if (!isNaN(v) && v > 0) setQuantity(v);
  };
  const addToCart = () => {
    try {
      const cart = loadCart() || [];
      let productToAdd = { ...product };

      if (
        variantImages.length > 1 &&
        product.variant_data &&
        product.variant_data[selectedImage]
      ) {
        const selectedVariantData = product.variant_data[selectedImage];
        productToAdd = {
          ...product,
          id: selectedVariantData.id || product.id,
          sku: selectedVariantData.sku,
          title: selectedVariantData.title || product.title,
          image_url: selectedVariantData.image_url,
          variant_info: {
            selected_sku: selectedVariantData.sku,
            selected_image: selectedVariantData.image_url,
            parent_id: product.id,
            variant_index: selectedImage,
          },
        };
      }

      const cartItemId = productToAdd.variant_info
        ? `${product.id}-${productToAdd.variant_info.selected_sku}`
        : productToAdd.id;

      const idx = cart.findIndex((item) => item.cart_id === cartItemId);

      if (idx >= 0) {
        cart[idx].quantity = (cart[idx].quantity || 1) + quantity;
      } else {
        cart.push({
          ...productToAdd,
          cart_id: cartItemId,
          quantity,
          image:
            productToAdd.image_url ||
            productToAdd.image_url_s ||
            generatePlaceholderImageUrl(productToAdd.title || 'Product'),
          category: productToAdd.category,
        });
      }

      saveCart(cart);

      const variantText = productToAdd.variant_info
        ? ` (${productToAdd.variant_info.selected_sku})`
        : '';
      showSnackbar(`${productToAdd.title}${variantText} added to cart!`);
    } catch {
      showSnackbar('Failed to add item to cart');
    }
  };

  if (loading) {
    return <ProductDetailSkeleton />;
  }
  if (error || !product) {
    return (
      <Container maxWidth='lg' sx={{ py: 8 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <Typography color='error'>{error || 'Product not found'}</Typography>
        </Box>
      </Container>
    );
  }

  // Images and variants
  const variantImages =
    Array.isArray(product.variant_images) && product.variant_images.length > 0
      ? product.variant_images
      : [
        product.image_url_s ||
          product.image_url ||
          generatePlaceholderImageUrl(product.title),
      ];
  const currentImage = variantImages[selectedImage] || variantImages[0];
  const price = product.price || product.price_d || product.price_f || 0;
  const formattedPrice = formatPrice(price);
  const rating = product.rating || (Math.random() * 2 + 3).toFixed(1);
  const reviewCount = Array.isArray(product.attribute_reviews_ss)
    ? product.attribute_reviews_ss.length
    : product.reviewCount || Math.floor(Math.random() * 50) + 1;
  const productImage =
    product.image_url_s ||
    product.image_url ||
    generatePlaceholderImageUrl(product.title || 'Product');

  return (
    <Container maxWidth='lg' sx={{ py: 3 }}>
      {/* Breadcrumb navigation */}
      <Breadcrumbs
        separator='›'
        aria-label='breadcrumb'
        sx={{
          mb: 3,
          '& .MuiBreadcrumbs-separator': {
            mx: 1,
          },
          '& a': {
            color: 'primary.main',
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' },
          },
        }}
      >
        <Link to='/'>Home</Link>
        {product.category && (
          <Link to={`/category/${encodeURIComponent(product.category)}`}>
            {product.category}
          </Link>
        )}
        <Typography color='text.secondary'>{product.title}</Typography>
      </Breadcrumbs>

      {/* Main product section - Material Design Card */}
      <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden', mb: 4 }}>
        <Grid container sx={{ minHeight: '400px' }}>
          {/* Left: Image Gallery - Reduced width */}
          <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
            <Box
              sx={{
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                flex: 1,
              }}
            >
              {/* Main image - Takes up all available space */}
              <Paper
                elevation={0}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  overflow: 'hidden',
                  bgcolor: 'grey.50',
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box
                  sx={{
                    aspectRatio: '1/1',
                    maxHeight: '300px',
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 1,
                    cursor: 'pointer',
                    position: 'relative',
                    '&:hover': {
                      '& .zoom-indicator': { opacity: 1 },
                    },
                  }}
                  onClick={() => setImageModalOpen(true)}
                >
                  <Box
                    component='img'
                    src={currentImage}
                    alt={product.title}
                    sx={{
                      maxHeight: '100%',
                      maxWidth: '100%',
                      objectFit: 'contain',
                      transition: 'transform 0.2s ease-in-out',
                      '&:hover': { transform: 'scale(1.02)' },
                    }}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = productImage;
                    }}
                  />

                  {/* Zoom indicator */}
                  <Chip
                    label='Click to enlarge'
                    size='small'
                    className='zoom-indicator'
                    sx={{
                      position: 'absolute',
                      bottom: 6,
                      right: 6,
                      opacity: 0,
                      transition: 'opacity 0.2s ease-in-out',
                      bgcolor: 'rgba(0,0,0,0.8)',
                      color: 'white',
                      fontSize: '0.6rem',
                    }}
                  />
                </Box>
              </Paper>
            </Box>
          </Grid>

          {/* Right: Product Information - Expanded width */}
          <Grid item xs={12} md={8} sx={{ display: 'flex' }}>
            <Box
              sx={{
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                flex: 1,
              }}
            >
              {/* Combined Product Info and Pricing Section */}
              <Paper
                variant='outlined'
                sx={{
                  p: 2.5,
                  bgcolor: 'grey.50',
                  borderRadius: 2,
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
              >
                {/* Product title and brand */}
                <Typography
                  variant='h5'
                  component='h1'
                  sx={{
                    fontWeight: 500,
                    lineHeight: 1.2,
                    mb: 1,
                    fontSize: '1.4rem',
                  }}
                >
                  {product.title}
                </Typography>

                {/* Brand and Ratings in same row */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    mb: 1.5,
                  }}
                >
                  <Typography
                    variant='body2'
                    color='primary'
                    sx={{
                      fontSize: '0.875rem',
                      '&:hover': {
                        textDecoration: 'underline',
                        cursor: 'pointer',
                      },
                    }}
                  >
                    <Link
                      to={`/manufacturer/${product.manufacturer}`}
                      style={{ color: 'inherit', textDecoration: 'none' }}
                    >
                      Brand: {product.manufacturer}
                    </Link>
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Rating
                      value={Number(rating)}
                      precision={0.5}
                      readOnly
                      size='small'
                    />
                    <Typography
                      variant='body2'
                      color='primary'
                      sx={{
                        cursor: 'pointer',
                        fontSize: '0.8rem',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      ({reviewCount})
                    </Typography>
                  </Box>
                </Box>

                {/* Variant selector - Linear and scrollable */}
                {variantImages.length > 1 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant='subtitle2'
                      sx={{ fontWeight: 600, mb: 1, fontSize: '0.8rem' }}
                    >
                      Available Options ({variantImages.length})
                    </Typography>

                    {/* Linear variant grid - Horizontal scrollable */}
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1,
                        overflowX: 'auto',
                        pb: 1,
                        '&::-webkit-scrollbar': {
                          height: '6px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          backgroundColor: 'rgba(0,0,0,0.2)',
                          borderRadius: '3px',
                        },
                      }}
                    >
                      {variantImages.map((img, idx) => {
                        const variantInfo = product.variant_data?.[idx] || {
                          sku: `Variant ${idx + 1}`,
                        };
                        const isSelected = selectedImage === idx;

                        return (
                          <Paper
                            key={`variant-${idx}`}
                            elevation={isSelected ? 3 : 1}
                            onClick={() => setSelectedImage(idx)}
                            sx={{
                              p: 0.5,
                              cursor: 'pointer',
                              transition: 'all 0.2s ease-in-out',
                              border: isSelected ? 2 : 1,
                              borderColor: isSelected
                                ? 'primary.main'
                                : 'divider',
                              borderStyle: 'solid',
                              minWidth: '60px',
                              flexShrink: 0,
                              '&:hover': {
                                elevation: 2,
                                transform: 'translateY(-1px)',
                              },
                            }}
                          >
                            <Box
                              sx={{
                                width: '50px',
                                height: '50px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mb: 0.5,
                              }}
                            >
                              <Box
                                component='img'
                                src={img}
                                alt={variantInfo.sku}
                                sx={{
                                  maxWidth: '100%',
                                  maxHeight: '100%',
                                  objectFit: 'contain',
                                }}
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = productImage;
                                }}
                              />
                            </Box>
                            <Typography
                              variant='caption'
                              sx={{
                                fontSize: '0.55rem',
                                fontWeight: isSelected ? 600 : 400,
                                color: isSelected
                                  ? 'primary.main'
                                  : 'text.secondary',
                                textAlign: 'center',
                                display: 'block',
                                fontFamily: 'monospace',
                                lineHeight: 1,
                                width: '50px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                              title={variantInfo.sku}
                            >
                              {variantInfo.sku}
                            </Typography>
                          </Paper>
                        );
                      })}
                    </Box>
                  </Box>
                )}

                {/* Price and In Stock in same row */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    mb: 2,
                  }}
                >
                  <Typography
                    variant='h4'
                    color='text.primary'
                    sx={{
                      fontWeight: 600,
                      fontSize: '1.6rem',
                    }}
                  >
                    {formattedPrice}
                  </Typography>

                  <Typography
                    variant='body2'
                    color='success.main'
                    sx={{ fontWeight: 600, fontSize: '0.875rem' }}
                  >
                    ✓ In Stock • FREE delivery
                  </Typography>
                </Box>

                <Divider sx={{ my: 1.5 }} />

                {/* Quantity and Add to Cart */}
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}
                >
                  <Typography
                    variant='body2'
                    sx={{ fontWeight: 600, fontSize: '0.875rem' }}
                  >
                    Qty:
                  </Typography>
                  <TextField
                    select
                    value={quantity}
                    onChange={handleQuantityChange}
                    SelectProps={{ native: true }}
                    size='small'
                    variant='outlined'
                    sx={{ minWidth: 70 }}
                  >
                    {[...Array(10).keys()].map((i) => (
                      <option key={i + 1} value={i + 1}>
                        {i + 1}
                      </option>
                    ))}
                  </TextField>

                  <Button
                    variant='contained'
                    onClick={addToCart}
                    className='lw-cart-add-trigger'
                    sx={{
                      flex: 1,
                      py: 1,
                      fontWeight: 600,
                      textTransform: 'none',
                      fontSize: '0.875rem',
                    }}
                  >
                    Add to Cart
                  </Button>
                </Box>
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* FAQ Section - Moved to its own row */}
      {isFaqVisible && (
        <Paper
          elevation={1}
          sx={{
            borderRadius: 2,
            overflow: 'hidden',
            mb: 4,
          }}
        >
          <Paper
            variant='outlined'
            sx={{
              p: 0,
              bgcolor: 'grey.50',
              borderRadius: 2,
              border: 'none',
            }}
          >
            <Box sx={{ height: '300px' }}>
              <ErrorBoundary
                componentName='QnAWidget'
                fallbackComponent={(props) => (
                  <EmbeddedComponentError
                    {...props}
                    title='FAQ Widget Failed to Load'
                    message='Unable to load the FAQ widget. Please try again later.'
                    minHeight='250px'
                  />
                )}
                onError={(error, info) =>
                  logError(error, {
                    componentName: 'QnAWidget',
                    productId: product?.id,
                    componentStack: info?.componentStack,
                  })
                }
              >
                <LW_Embeddable
                  id='qna-template-1'
                  mockMode={true}
                  productId={product?.unique_id || '1251002'}
                />
              </ErrorBoundary>
            </Box>
          </Paper>
        </Paper>
      )}

      {/* Information Tabs */}
      <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Tabs value={activeTab} onChange={handleTabChange} variant='fullWidth'>
          <Tab label='Description' sx={{ fontSize: '0.875rem' }} />
          <Tab label='Specifications' sx={{ fontSize: '0.875rem' }} />
          <Tab label='Resources' sx={{ fontSize: '0.875rem' }} />
        </Tabs>

        <Box sx={{ p: 2 }}>
          {/* Description Tab */}
          {activeTab === 0 && (
            <Box>
              <Typography
                variant='body2'
                sx={{ lineHeight: 1.6, fontSize: '0.875rem' }}
              >
                {product.description ||
                  'No detailed description available for this product.'}
              </Typography>
            </Box>
          )}

          {/* Specifications Tab */}
          {activeTab === 1 && (
            <Grid container spacing={2}>
              {[
                { label: 'SKU', value: product.sku },
                { label: 'Brand', value: product.manufacturer },
                { label: 'Category', value: product.category },
                { label: 'Price', value: formattedPrice },
                {
                  label: 'Weight',
                  value: product.weight
                    ? `${product.weight} lbs`
                    : 'Not specified',
                },
                { label: 'Status', value: product.status },
              ]
                .filter((item) => item.value)
                .map((item, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Paper variant='outlined' sx={{ p: 1.5, height: '100%' }}>
                      <Typography
                        variant='caption'
                        color='primary'
                        sx={{ fontWeight: 600, mb: 0.5, fontSize: '0.75rem' }}
                      >
                        {item.label}
                      </Typography>
                      <Typography
                        variant='body2'
                        sx={{
                          fontFamily:
                            item.label === 'SKU' ? 'monospace' : 'inherit',
                          fontSize: '0.875rem',
                        }}
                      >
                        {item.value}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
            </Grid>
          )}

          {/* Resources Tab */}
          {activeTab === 2 && (
            <Box>
              <Typography
                variant='h6'
                sx={{ fontWeight: 600, mb: 2, fontSize: '1rem' }}
              >
                Manufacturer Documents
              </Typography>
              <Grid container spacing={2}>
                {[
                  { title: 'Warranty Information', fileSize: '1.2 MB' },
                  { title: 'Installation Guide', fileSize: '842 KB' },
                  { title: 'Specification Sheet', fileSize: '3.5 MB' },
                ].map((doc, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Paper
                      variant='outlined'
                      sx={{
                        p: 1.5,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          elevation: 2,
                          transform: 'translateY(-2px)',
                          bgcolor: 'primary.50',
                        },
                      }}
                    >
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}
                      >
                        <img
                          src={pdfIcon}
                          alt='PDF'
                          style={{ width: 20, height: 20, marginRight: 8 }}
                        />
                        <Typography
                          variant='subtitle2'
                          sx={{ fontWeight: 600, fontSize: '0.875rem' }}
                        >
                          {doc.title}
                        </Typography>
                      </Box>
                      <Typography
                        variant='caption'
                        color='text.secondary'
                        sx={{ fontSize: '0.75rem' }}
                      >
                        PDF • {doc.fileSize}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Snackbar for cart confirmation */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity='success'
          variant='filled'
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Mobile floating action button */}
      {isMobile && (
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 16,
            left: 16,
            right: 16,
            p: 1.5,
            zIndex: 1000,
            borderRadius: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography
              variant='h6'
              color='error.main'
              sx={{ fontWeight: 600, fontSize: '1.25rem' }}
            >
              {formattedPrice}
            </Typography>
            <Button
              variant='contained'
              onClick={addToCart}
              className='lw-cart-add-trigger'
              sx={{
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '0.875rem',
              }}
            >
              Add to Cart
            </Button>
          </Box>
        </Paper>
      )}

      {/* Image Modal for variant images */}
      <Dialog
        open={imageModalOpen}
        onClose={() => setImageModalOpen(false)}
        maxWidth='md'
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            borderRadius: 1,
            boxShadow: 5,
            p: 1,
            height: { xs: '90vh', sm: '80vh' },
            maxHeight: '90vh',
          },
        }}
      >
        <DialogContent
          sx={{
            p: 0,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Close button and SKU info */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
            }}
          >
            {/* Current variant SKU display */}
            <Box sx={{ flex: 1 }}>
              {product.variant_data && product.variant_data[selectedImage] && (
                <Typography
                  variant='h6'
                  sx={{
                    fontWeight: 600,
                    color: 'primary.main',
                    fontFamily: 'monospace',
                  }}
                >
                  SKU: {product.variant_data[selectedImage].sku}
                </Typography>
              )}
            </Box>
            <IconButton onClick={() => setImageModalOpen(false)}>
              <Typography variant='h6'>×</Typography>
            </IconButton>
          </Box>

          {/* Main image container */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              p: 2,
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            <img
              src={variantImages[selectedImage] || productImage}
              alt={`${
                product.variant_data?.[selectedImage]?.sku || 'Product'
              } - ${product.title}`}
              style={{
                maxHeight: '100%',
                maxWidth: '100%',
                objectFit: 'contain',
              }}
            />

            {/* Image navigation arrows */}
            {variantImages.length > 1 && (
              <>
                <IconButton
                  sx={{
                    position: 'absolute',
                    left: 16,
                    backgroundColor: 'rgba(255,255,255,0.8)',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' },
                    boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                  }}
                  onClick={() => {
                    setSelectedImage((prev) =>
                      prev > 0 ? prev - 1 : variantImages.length - 1
                    );
                  }}
                >
                  <NavigateBeforeIcon />
                </IconButton>
                <IconButton
                  sx={{
                    position: 'absolute',
                    right: 16,
                    backgroundColor: 'rgba(255,255,255,0.8)',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' },
                    boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                  }}
                  onClick={() => {
                    setSelectedImage((prev) =>
                      prev < variantImages.length - 1 ? prev + 1 : 0
                    );
                  }}
                >
                  <NavigateNextIcon />
                </IconButton>
              </>
            )}
          </Box>

          {/* Thumbnails row with SKU labels */}
          {variantImages.length > 1 && (
            <Box
              sx={{
                display: 'flex',
                overflowX: 'auto',
                gap: 1.5,
                p: 2,
                justifyContent: 'center',
                '&::-webkit-scrollbar': {
                  height: '8px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(0,0,0,0.2)',
                  borderRadius: '4px',
                },
              }}
            >
              {variantImages.map((img, idx) => {
                const variantInfo =
                  product.variant_data && product.variant_data[idx]
                    ? product.variant_data[idx]
                    : { sku: `Item ${idx + 1}` };

                return (
                  <Box
                    key={`modal-thumb-${idx}`}
                    onClick={() => setSelectedImage(idx)}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                  >
                    <Box
                      sx={{
                        width: 70,
                        height: 70,
                        flexShrink: 0,
                        border:
                          selectedImage === idx ? '2px solid' : '1px solid',
                        borderColor:
                          selectedImage === idx ? 'primary.main' : 'grey.300',
                        borderRadius: 1,
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        '&:hover': {
                          borderColor: 'primary.main',
                        },
                      }}
                    >
                      <img
                        src={img}
                        alt={`${variantInfo.sku} - Variant ${idx + 1}`}
                        style={{
                          maxHeight: '90%',
                          maxWidth: '90%',
                          objectFit: 'contain',
                        }}
                      />
                    </Box>
                    <Typography
                      variant='caption'
                      sx={{
                        mt: 0.5,
                        fontSize: '0.7rem',
                        fontWeight: selectedImage === idx ? 600 : 400,
                        color:
                          selectedImage === idx
                            ? 'primary.main'
                            : 'text.secondary',
                        textAlign: 'center',
                        maxWidth: 80,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontFamily: 'monospace',
                      }}
                      title={variantInfo.sku}
                    >
                      {variantInfo.sku}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
}
